import React, { useCallback, useEffect, useMemo } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Empty,
  Row,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  CheckSquareOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  SyncOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useDailyTaskTracking } from '../hooks/useDailyTaskTracking';
import { useExportDailyTaskTrackingPdf } from '../hooks/useExportDailyTaskTrackingPdf';
import { useExportTasks } from '../hooks/useExportTasks';
import { useExportTasksPdf } from '../hooks/useExportTasksPdf';
import { useTaskItems } from '../hooks/useTaskItems';
import type { GetDailyTaskTrackingParams } from '../api/dailyTaskTracking';
import type { DailyTaskTrackingRow } from '../api/dailyTaskTracking';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { usePermission } from '../../permissions/PermissionsContext';
import { defaultDailyTrackingDateRange } from '../api/dailyTaskTracking';
import type { TaskItemListItem, GetTaskItemsParams, ExportTasksParams } from '../types';
import { TASK_TYPE_SHORT_LABELS, TASK_TYPE_TAG_COLORS } from '../constants';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { DASHBOARD_FILTER_FIELDS } from '../../../routes/filterMaps';
import DailyTaskTrackingFilterBar, {
  type DailyTaskTrackingFilterState,
} from './DailyTaskTrackingFilterBar';
import PageScaffold from '../../../components/PageScaffold';
import { useNavigate } from 'react-router-dom';
import { appPath } from '../../../routes/paths';

dayjs.extend(utc);

const { Text } = Typography;

const cardBodyPadding = { padding: '16px 20px' };

const DailyTaskTrackingPage: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { isScoped, assignedSegmentIds, defaultSegmentIdsForApi } = useUserSegmentScope();
  const isAdmin = usePermission('administrator.access');
  const defaultFilters: DailyTaskTrackingFilterState = useMemo(() => ({
    dateRange: defaultDailyTrackingDateRange(),
    segmentIds: [],
    contactCategoryIds: [],
    assigneeIds: [],
  }), []);

  const transformOptions = useMemo(() => ({
    transform: {
      deserialize: (params: URLSearchParams) => {
        const from = params.get('fromUtc');
        const to = params.get('toUtc');
        if (from && to) {
          return {
            dateRange: [dayjs(from), dayjs(to)] as [Dayjs, Dayjs],
          } as Partial<DailyTaskTrackingFilterState>;
        }
        return {};
      },
      serialize: (f: DailyTaskTrackingFilterState) => {
        return {
          fromUtc: f.dateRange[0].utc().startOf('day').toISOString(),
          toUtc: f.dateRange[1].utc().endOf('day').toISOString(),
        };
      },
    },
  }), []);

  const [filters, setFilters, , silentSetFilters] = useUrlListFilter(
    defaultFilters,
    DASHBOARD_FILTER_FIELDS,
    transformOptions,
  );

  useEffect(() => {
    if (!isScoped) {
      return;
    }
    silentSetFilters((prev: DailyTaskTrackingFilterState) => ({
      ...prev,
      segmentIds: defaultSegmentIdsForApi(),
      assigneeIds: [],
    }));
  }, [isScoped, assignedSegmentIds.join(','), defaultSegmentIdsForApi, silentSetFilters]);

  const queryParams = useMemo((): GetDailyTaskTrackingParams => {
    const params: GetDailyTaskTrackingParams = {};
    const [from, to] = filters.dateRange;
    params.fromUtc = from.utc().startOf('day').toISOString();
    params.toUtc = to.utc().endOf('day').toISOString();
    const segmentIds = defaultSegmentIdsForApi(filters.segmentIds);
    if (segmentIds.length > 0) {
      params.segmentIds = segmentIds;
    }
    if (filters.contactCategoryIds.length > 0) {
      params.contactCategoryIds = filters.contactCategoryIds;
    }
    if (isAdmin && filters.assigneeIds.length > 0) {
      params.assigneeIds = filters.assigneeIds;
    }
    return params;
  }, [filters, defaultSegmentIdsForApi, isAdmin]);

  const { data, isLoading, isError, isFetching, error, refetch, isPlaceholderData } = useDailyTaskTracking(queryParams);

  const pdfExportMutation = useExportDailyTaskTrackingPdf();
  const completedTasksExportMutation = useExportTasks();
  const completedTasksPdfExportMutation = useExportTasksPdf();

  const handleExportPdf = useCallback(() => {
    const params: GetDailyTaskTrackingParams = {
      fromUtc: queryParams.fromUtc,
      toUtc: queryParams.toUtc,
      segmentIds: queryParams.segmentIds,
      contactCategoryIds: queryParams.contactCategoryIds,
      assigneeIds: queryParams.assigneeIds,
    };
    pdfExportMutation.mutate(params);
  }, [queryParams, pdfExportMutation]);

  const completedTaskExportParams = useMemo((): ExportTasksParams => {
    const dueFrom = filters.dateRange[0].format('YYYY-MM-DD');
    const dueTo = filters.dateRange[1].format('YYYY-MM-DD');
    const segmentIds = defaultSegmentIdsForApi(filters.segmentIds);
    return {
      status: 3,
      dueFrom,
      dueTo,
      segmentIds: segmentIds.length > 0 ? segmentIds : undefined,
      contactCategoryIds: filters.contactCategoryIds.length > 0 ? filters.contactCategoryIds : undefined,
    };
  }, [filters.dateRange, filters.segmentIds, filters.contactCategoryIds, defaultSegmentIdsForApi]);

  const handleExportCompletedExcel = useCallback(() => {
    completedTasksExportMutation.mutate(completedTaskExportParams);
  }, [completedTasksExportMutation, completedTaskExportParams]);

  const handleExportCompletedPdf = useCallback(() => {
    completedTasksPdfExportMutation.mutate(completedTaskExportParams);
  }, [completedTasksPdfExportMutation, completedTaskExportParams]);

  const handleExportMenuClick: MenuProps['onClick'] = useCallback(
    ({ key }: { key: string }) => {
      if (key === 'summary-pdf') handleExportPdf();
      else if (key === 'completed-excel') handleExportCompletedExcel();
      else if (key === 'completed-pdf') handleExportCompletedPdf();
    },
    [handleExportPdf, handleExportCompletedExcel, handleExportCompletedPdf]
  );

  const dashboardExportMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'summary',
        label: 'Summary Report',
        type: 'group',
        children: [
          { key: 'summary-pdf', icon: <FilePdfOutlined />, label: 'PDF (.pdf)' },
        ],
      },
      {
        key: 'completed',
        label: 'Completed Tasks',
        type: 'group',
        children: [
          { key: 'completed-excel', icon: <FileExcelOutlined />, label: 'Excel (.xlsx)' },
          { key: 'completed-pdf', icon: <FilePdfOutlined />, label: 'PDF (.pdf)' },
        ],
      },
    ],
    []
  );

  const taskParams = useMemo((): GetTaskItemsParams => {
    const dueFrom = filters.dateRange[0].format('YYYY-MM-DD');
    const dueTo = filters.dateRange[1].format('YYYY-MM-DD');
    const segmentIds = defaultSegmentIdsForApi(filters.segmentIds);
    return {
      status: 3,
      dueFrom,
      dueTo,
      pageSize: 100,
      segmentIds: segmentIds.length > 0 ? segmentIds : undefined,
      contactCategoryIds: filters.contactCategoryIds.length > 0 ? filters.contactCategoryIds : undefined,
    };
  }, [filters.dateRange, filters.segmentIds, filters.contactCategoryIds, defaultSegmentIdsForApi]);

  const { data: completedTasksData, isLoading: completedTasksLoading } = useTaskItems(taskParams, {
    enabled: !!data,
  });

  const sectionCardStyle = {
    height: '100%',
    background: token.colorBgContainer,
    borderRadius: token.borderRadiusLG,
  };

  const handleRowClick = useCallback(
    (record: DailyTaskTrackingRow) => {
      const date = dayjs(record.date);
      const dueFrom = date.format('YYYY-MM-DD');
      const dueTo = date.format('YYYY-MM-DD');
      navigate(`${appPath('/tasks')}?dueFrom=${dueFrom}&dueTo=${dueTo}`);
    },
    [navigate]
  );

  const columns: ColumnsType<DailyTaskTrackingRow> = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        render: (date: string) => (
          <Button type="link" size="small" style={{ padding: 0 }} icon={<EyeOutlined />}>
            {dayjs(date).format('MMM D, YYYY')}
          </Button>
        ),
      },
      {
        title: 'Total',
        dataIndex: 'totalTasks',
        key: 'totalTasks',
        width: 72,
        align: 'right',
        sorter: (a, b) => a.totalTasks - b.totalTasks,
      },
      {
        title: 'Completed',
        dataIndex: 'completedTasks',
        key: 'completedTasks',
        width: 96,
        align: 'right',
        render: (v: number) => (
          <Text style={{ color: token.colorSuccess }}>{v}</Text>
        ),
        sorter: (a, b) => a.completedTasks - b.completedTasks,
      },
      {
        title: 'In Progress',
        dataIndex: 'inProgressTasks',
        key: 'inProgressTasks',
        width: 96,
        align: 'right',
        render: (v: number) => (
          <Text style={{ color: token.colorPrimary }}>{v}</Text>
        ),
      },
      {
        title: 'Overdue',
        dataIndex: 'overdueTasks',
        key: 'overdueTasks',
        width: 80,
        align: 'right',
        render: (v: number) => (
          <Text style={{ color: v > 0 ? token.colorError : undefined }}>{v}</Text>
        ),
      },
      {
        title: 'Blocked',
        dataIndex: 'blockedTasks',
        key: 'blockedTasks',
        width: 80,
        align: 'right',
        render: (v: number) => (
          <Text style={{ color: token.colorWarning }}>{v}</Text>
        ),
      },
      {
        title: 'Cancelled',
        dataIndex: 'cancelledTasks',
        key: 'cancelledTasks',
        width: 88,
        align: 'right',
      },
      {
        title: 'Completion %',
        dataIndex: 'completionRate',
        key: 'completionRate',
        width: 112,
        align: 'right',
        render: (v: number) => (
          <Text strong style={{ color: v >= 50 ? token.colorSuccess : token.colorWarning }}>
            {v}%
          </Text>
        ),
        sorter: (a, b) => a.completionRate - b.completionRate,
      },
    ],
    [token, handleRowClick]
  );

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.dailyRows.map((row) => ({
      date: dayjs(row.date).format('MMM D'),
      total: row.totalTasks,
      completed: row.completedTasks,
    }));
  }, [data]);

  const staleNote = isPlaceholderData ? (
    <Text type="secondary" style={{ fontSize: 12 }}>
      Showing previous results while updating…
    </Text>
  ) : undefined;

  const dateRangeLabel = `${filters.dateRange[0].format('MMM D')} – ${filters.dateRange[1].format('MMM D, YYYY')}`;

  const loadingSkeleton = (
    <>
      <Col span={24}>
        <Card bordered={false} style={{ borderRadius: token.borderRadiusLG }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      </Col>
      <Col span={24}>
        <Card bordered={false} style={{ borderRadius: token.borderRadiusLG }}>
          <Skeleton active title={{ width: '40%' }} paragraph={{ rows: 10 }} />
        </Card>
      </Col>
    </>
  );

  if (isLoading && data === undefined) {
    return (
      <PageScaffold
        title="Daily Task Tracking"
        subtitle="Daily task progress, completion trends, and performance summary."
        filters={
          <DailyTaskTrackingFilterBar value={filters} onChange={setFilters} />
        }
      >
        <Row gutter={[16, 16]}>{loadingSkeleton}</Row>
      </PageScaffold>
    );
  }

  if (isError) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load Daily Task Tracking.';
    return (
      <PageScaffold
        title="Daily Task Tracking"
        subtitle="Daily task progress, completion trends, and performance summary."
        filters={
          <DailyTaskTrackingFilterBar value={filters} onChange={setFilters} />
        }
      >
        <Col span={24}>
          <Alert
            type="error"
            message="Could not load Daily Task Tracking"
            description={errorMessage}
            showIcon
            action={
              <Button onClick={() => refetch()} type="primary" size="small" icon={<ReloadOutlined />}>
                Retry
              </Button>
            }
          />
        </Col>
      </PageScaffold>
    );
  }

  if (!data) {
    return (
      <PageScaffold
        title="Daily Task Tracking"
        subtitle="Daily task progress, completion trends, and performance summary."
        filters={
          <DailyTaskTrackingFilterBar value={filters} onChange={setFilters} />
        }
      >
        <Col span={24}>
          <Empty description="No data returned." />
        </Col>
      </PageScaffold>
    );
  }

  const { summary } = data;

  return (
    <div style={{ opacity: isFetching && !isLoading ? 0.92 : 1, transition: 'opacity 0.2s' }}>
      <PageScaffold
        title="Daily Task Tracking"
        subtitle="Daily task progress, completion trends, and performance summary."
        actions={
          <Dropdown
            menu={{ items: dashboardExportMenuItems, onClick: handleExportMenuClick }}
            trigger={['click']}
          >
            <Button
              icon={<DownloadOutlined />}
              loading={pdfExportMutation.isPending || completedTasksExportMutation.isPending || completedTasksPdfExportMutation.isPending}
            >
              Export
            </Button>
          </Dropdown>
        }
        filters={
          <DailyTaskTrackingFilterBar value={filters} onChange={setFilters} />
        }
      >
        {staleNote && (
          <div style={{ marginBottom: 16 }}>{staleNote}</div>
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="Total Tasks"
                value={summary.totalTasks}
                prefix={<UnorderedListOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                In selected period
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="Completed"
                value={summary.completedTasks}
                suffix={<Text style={{ fontSize: 14, color: token.colorSuccess }}>/ {summary.totalTasks} ({summary.completionRate}%)</Text>}
                valueStyle={{ color: token.colorSuccess }}
                prefix={<CheckSquareOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Completion rate: {summary.completionRate}%
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="In Progress"
                value={summary.inProgressTasks}
                prefix={<SyncOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Currently active
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="Overdue"
                value={summary.overdueTasks}
                valueStyle={{ color: summary.overdueTasks > 0 ? token.colorError : undefined }}
                prefix={<CloseCircleOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Past due date, not completed
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="Blocked"
                value={summary.blockedTasks}
                prefix={<StopOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Status: blocked
              </Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card bordered={false} style={sectionCardStyle} styles={{ body: cardBodyPadding }}>
              <Statistic
                title="Cancelled"
                value={summary.cancelledTasks}
                prefix={<MinusCircleOutlined />}
              />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Status: cancelled
              </Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card
              bordered={false}
              title={
                <Space>
                  <CheckSquareOutlined style={{ color: token.colorPrimary }} />
                  <span>Daily Completion Trend</span>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{dateRangeLabel}</Text>
                </Space>
              }
              style={sectionCardStyle}
              styles={{ body: { ...cardBodyPadding, minHeight: 300 } }}
            >
              {chartData.length === 0 ? (
                <Empty description="No task data for the selected period" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#666' }}
                      tickLine={false}
                      axisLine={{ stroke: '#f0f0f0' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#666' }}
                      tickLine={false}
                      axisLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                      contentStyle={{ borderRadius: 6, border: '1px solid #f0f0f0' }}
                    />
                    <Bar dataKey="total" fill={token.colorBgElevated} stroke={token.colorBorder} strokeWidth={1} radius={[4, 4, 0, 0]} name="Total" />
                    <Bar dataKey="completed" fill={token.colorSuccess} radius={[4, 4, 0, 0]} name="Completed" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card
              bordered={false}
              title={
                <Space>
                  <UnorderedListOutlined style={{ color: token.colorPrimary }} />
                  <span>Daily Breakdown</span>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{data.dailyRows.length} days · {dateRangeLabel}</Text>
                </Space>
              }
              style={sectionCardStyle}
              styles={{ body: { ...cardBodyPadding, paddingTop: 12 } }}
            >
              <Table<DailyTaskTrackingRow>
                rowKey={(row) => row.date}
                columns={columns}
                dataSource={data.dailyRows}
                onRow={(record) => ({
                  onClick: () => handleRowClick(record),
                  style: { cursor: 'pointer' },
                })}
                pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `${total} days` }}
                size="middle"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No tasks in this period"
                    />
                  ),
                }}
                scroll={{ x: 'max-content' }}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong>{summary.totalTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text strong style={{ color: token.colorSuccess }}>{summary.completedTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <Text strong>{summary.inProgressTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <Text strong style={{ color: summary.overdueTasks > 0 ? token.colorError : undefined }}>{summary.overdueTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <Text strong>{summary.blockedTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <Text strong>{summary.cancelledTasks}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <Text strong style={{ color: summary.completionRate >= 50 ? token.colorSuccess : token.colorWarning }}>
                          {summary.completionRate}%
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card
              bordered={false}
              title={
                <Space>
                  <CheckSquareOutlined style={{ color: token.colorSuccess }} />
                  <span>Completed Tasks</span>
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    {completedTasksData?.totalCount ?? '…'} tasks · {dateRangeLabel}
                  </Text>
                </Space>
              }
              style={sectionCardStyle}
              styles={{ body: { ...cardBodyPadding, paddingTop: 12 } }}
            >
              {completedTasksLoading ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : completedTasksData && completedTasksData.items.length > 0 ? (
                <Table<TaskItemListItem>
                  rowKey="id"
                  dataSource={completedTasksData.items}
                  pagination={false}
                  size="small"
                  columns={[
                    {
                      title: 'Task',
                      dataIndex: 'title',
                      key: 'title',
                      render: (title: string, record) => {
                        const dateStr = record.dueDate ? dayjs(record.dueDate).format('YYYY-MM-DD') : '';
                        return (
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, textAlign: 'left' }}
                            onClick={() => navigate(dateStr ? `${appPath('/tasks')}?dueFrom=${dateStr}&dueTo=${dateStr}` : appPath('/tasks'))}
                          >
                            {title}
                          </Button>
                        );
                      },
                    },
                    {
                      title: 'Due Date',
                      dataIndex: 'dueDate',
                      key: 'dueDate',
                      width: 130,
                      render: (date: string | null) => date ? dayjs(date).format('MMM D, YYYY') : '—',
                    },
                    {
                      title: 'Type',
                      dataIndex: 'taskType',
                      key: 'taskType',
                      width: 110,
                      render: (type: number) => {
                        const t = type as 0 | 1 | 2;
                        return (
                          <Tag color={TASK_TYPE_TAG_COLORS[t] ?? 'default'}>
                            {TASK_TYPE_SHORT_LABELS[t] ?? '—'}
                          </Tag>
                        );
                      },
                    },
                    {
                      title: 'Assignee',
                      dataIndex: 'assigneeDisplayName',
                      key: 'assigneeDisplayName',
                      width: 150,
                      render: (name: string | null) => name ?? '—',
                    },
                  ]}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No completed tasks in this period"
                      />
                    ),
                  }}
                />
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No completed tasks in this period"
                />
              )}
            </Card>
          </Col>
        </Row>
      </PageScaffold>
    </div>
  );
};

export default DailyTaskTrackingPage;
